import { useQueryClient } from "@tanstack/react-query";
import type { Category, Channel, CommunityStructure } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { toast } from "../../components/ui/Toast";

/*
 * Reordering channels and categories (members with Manage channels).
 * Indices are positions in the target list *before* the moving item is taken
 * out, which is what a drop indicator points at. Every visible channel gets a
 * dense position in display order, so the server's position sort matches.
 */

type Cat = string | null;

function lists(s: Pick<CommunityStructure, "categories" | "channels">) {
  const order: Cat[] = [null, ...s.categories.map((c) => c.id)];
  return order.map((cid) => ({ cid, items: s.channels.filter((c) => (c.categoryId ?? null) === cid) }));
}

export function channelLocation(s: Pick<CommunityStructure, "categories" | "channels">, id: string) {
  for (const l of lists(s)) {
    const i = l.items.findIndex((c) => c.id === id);
    if (i >= 0) return { categoryId: l.cid, index: i, length: l.items.length };
  }
  return null;
}

export function moveChannel(s: Pick<CommunityStructure, "categories" | "channels">, id: string, to: { categoryId: Cat; index: number }) {
  const ls = lists(s);
  const from = ls.find((l) => l.items.some((c) => c.id === id));
  const dest = ls.find((l) => l.cid === to.categoryId);
  if (!from || !dest) return null;
  const srcIdx = from.items.findIndex((c) => c.id === id);
  let index = to.index;
  if (from === dest && srcIdx < index) index -= 1;
  if (from === dest && srcIdx === index) return null;
  const [moving] = from.items.splice(srcIdx, 1);
  dest.items.splice(Math.max(0, Math.min(index, dest.items.length)), 0, moving);
  const channels: Channel[] = [];
  for (const l of ls) for (const c of l.items) channels.push({ ...c, categoryId: l.cid, position: channels.length });
  return { channels, items: channels.map((c) => ({ id: c.id, position: c.position, categoryId: c.categoryId })) };
}

export function moveCategory(s: Pick<CommunityStructure, "categories">, id: string, index: number) {
  const from = s.categories.findIndex((c) => c.id === id);
  if (from < 0) return null;
  const to = from < index ? index - 1 : index;
  if (to === from) return null;
  const next = [...s.categories];
  const [moving] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(to, next.length)), 0, moving);
  const categories: Category[] = next.map((c, i) => ({ ...c, position: i }));
  return { categories, items: categories.map((c) => ({ id: c.id, position: c.position })) };
}

/** One keyboard step for a channel: within its category, crossing into the neighbouring one at either end. */
export function stepChannel(s: Pick<CommunityStructure, "categories" | "channels">, id: string, dir: -1 | 1): { categoryId: Cat; index: number } | null {
  const ls = lists(s);
  const li = ls.findIndex((l) => l.items.some((c) => c.id === id));
  if (li < 0) return null;
  const i = ls[li].items.findIndex((c) => c.id === id);
  if (dir < 0) {
    if (i > 0) return { categoryId: ls[li].cid, index: i - 1 };
    return li > 0 ? { categoryId: ls[li - 1].cid, index: ls[li - 1].items.length } : null;
  }
  if (i < ls[li].items.length - 1) return { categoryId: ls[li].cid, index: i + 2 };
  return li < ls.length - 1 ? { categoryId: ls[li + 1].cid, index: 0 } : null;
}

/** Optimistic structure edits with rollback and a toast on failure. */
export function useStructureOrder(communityId: string) {
  const qc = useQueryClient();
  const key = ["structure", communityId];
  const apply = async (patch: Partial<Pick<CommunityStructure, "channels" | "categories">>, request: () => Promise<unknown>) => {
    await qc.cancelQueries({ queryKey: key });
    const prev = qc.getQueryData<CommunityStructure>(key);
    qc.setQueryData<CommunityStructure>(key, (old) => old && { ...old, ...patch });
    try {
      await request();
      return true;
    } catch (e) {
      qc.setQueryData(key, prev);
      toast.error(errorMessage(e, "Couldn't save the new order"));
      return false;
    }
  };
  return {
    channels: (r: NonNullable<ReturnType<typeof moveChannel>>) =>
      apply({ channels: r.channels }, () => api.put(`/api/communities/${communityId}/channels/order`, { items: r.items })),
    categories: (r: NonNullable<ReturnType<typeof moveCategory>>) =>
      apply({ categories: r.categories }, () => api.put(`/api/communities/${communityId}/categories/order`, { items: r.items })),
  };
}
